import h from 'hyperscript';

import selectedIcon from '@disy/cadenza-icons/check-small.svg';
import { ariaTrueFalse } from '@disy/lib/aria';
import { assert } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { ChangeEvent, on } from '@disy/lib/event-util';
import { icon } from '@disy/lib/icon';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { createButton } from '@disy/ui/button';
import { onFormData } from '@disy/ui/form-data';
import { createFormGroup } from '@disy/ui/form-group';
import { PLACEMENT, closePopup, setupPopupButton } from '@disy/ui/popup';
import { separateElements } from '@disy/ui/separator';

import { shouldColorSampleHaveOutline } from 'ui/color-disc/color-disc';
import type { SelectListOptionElement } from 'ui/select-list/select-list';
import { SelectList } from 'ui/select-list/select-list';
import { SliderNumberInput } from 'ui/slider-number-input/slider-number-input';

import type { Color, RgbaColor } from 'cadenza/utils/colors/colors';
import {
  COLORS,
  getColorWithOpacity,
  getOpacity,
  getOpacityDecimalValueFromPercentage,
  isColor,
  isDark,
  isRgbaColor,
  toHexColor,
  toRgbaColor,
} from 'cadenza/utils/colors/colors';

import './color-picker.css';
import i18n from './color-picker.properties';

const COMPONENT_NAME = 'd-color-picker';
const DEFAULT_CUSTOM_COLOR = '#cccccc';

interface ColorPickerOptions {
  /** The initial color to be set in the picker, in either hex or rgba format. Default is first palette color. */
  value?: Color;
  /** Allows to hide the dropdown and make it looks like icon button, default true */
  iconButton?: boolean;
  /** Indicates if an opacity control will be shown below the color list */
  withOpacityControl?: boolean;
  /** If set, an option is added with the specified text allowing the value to be set to undefined */
  undefinedValueLabel?: string;
  /** If true, then the ColorPicker is closed after the user has picked a color, otherwise it will stay open. */
  closeOnChange?: boolean;
}

export class ColorPicker extends HTMLElement {
  readonly #palette;
  readonly #returnType: 'rgba' | 'lowerHex' | 'upperHex';
  readonly #selectedColorElement;
  readonly #opacitySlider;
  readonly #undefinedValueLabel;
  readonly #closeOnChange;
  readonly #iconButton;
  #eventKeys: Teardown[] = [];
  #popupListener?: () => void;
  #currentColor?: RgbaColor; // the currently selected color, always in rgba, only used internally
  #undefinedValueButton?: HTMLButtonElement;

  /**
   * Pick a color out of a given color palette. In addition to the limited palette, the user can
   * also choose a custom color using the native browser color picker.
   *
   * The picker's value getter will return the currently selected color in the format of the `value`
   * param that was received at initialization. If the initial value was in rgba, then the getter
   * will return rgba. When hex was given initially, the casing of the returned hex color is
   * dependent on the casing of the given color palette. If it is uppercased, then the returned hex
   * will also be uppercased.
   *
   * The picker is often used with a currently selected color in rgba format and a hex palette,
   * because we persist colors in rgba format in the database while storing the palettes in hex in
   * "colors.js". [Decision in architects
   * guild](https://conf.disy.net/display/ENTW/Architecture+Guild+Meeting+Minutes+2020-07-23).
   */
  constructor(
    palette: readonly Color[],
    {
      value = palette[0],
      iconButton = false,
      withOpacityControl = false,
      undefinedValueLabel,
      closeOnChange = true,
    }: ColorPickerOptions = {},
  ) {
    super();
    assert(isColor(value), `Unsupported color ${value}`);
    if (isRgbaColor(value) || withOpacityControl) {
      this.#returnType = 'rgba';
    } else {
      this.#returnType = palette[0].toUpperCase() === palette[0] ? 'upperHex' : 'lowerHex';
    }
    this.#palette = palette.map((color) => color.toLowerCase() as Color);
    this.#selectedColorElement = h('.selected-color');
    this.#undefinedValueLabel = undefinedValueLabel;
    this.#closeOnChange = closeOnChange;
    this.#iconButton = iconButton;
    this.#opacitySlider = withOpacityControl ? this.#createOpacitySlider(value) : undefined;
    this.value = value;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'focus-outline', 'form-group-control');
    if (this.#iconButton) {
      this.classList.add('icon-button', 'button');
    }
    this.append(this.#selectedColorElement);
    if (!this.readOnly) {
      this.#setupPicker();
    }
  }

  disconnectedCallback() {
    this.#unregisterEvents();
  }

  #unregisterEvents() {
    this.#eventKeys = tearDown(this.#eventKeys);
    this.#popupListener?.();
  }

  #setupPicker() {
    this.setAttribute('role', 'button');
    this.tabIndex = 0;
    const label = document.getElementById(this.getAttribute('aria-labelledby')!);
    if (label) {
      // Event on wrapper auto-fieldset element
      this.#eventKeys.push(on(label, 'click', () => this.click()));
    }

    this.#popupListener = setupPopupButton(this, () => this.#createPopupContent(), {
      styleClass: [`${COMPONENT_NAME}--flyout`, 'd-stack-v', 'space-2'],
      placement: PLACEMENT.BOTTOM_START,
      hasSameWidthAsTarget: true,
    });

    this.#eventKeys.push(
      onFormData(this, (event) => {
        const name = this.name;
        if (!name || this.disabled) {
          return;
        }
        const value = this.value;
        if (value) {
          event.formData.set(name, value);
        }
      }),
    );
  }

  #createPopupContent() {
    return separateElements([
      this.#createUndefinedValueElement(),
      this.#createPaletteListElement(),
      this.#createCustomColorSection(),
      this.#createOpacityElement(),
    ]);
  }

  #dispatchChangeEventAndClosePopup() {
    this.dispatchEvent(new ChangeEvent());
    if (this.#closeOnChange) {
      closePopup(this);
    }
  }

  override focus(options: FocusOptions) {
    if (!this.disabled) {
      super.focus(options);
    }
  }

  #createUndefinedValueElement() {
    if (this.#undefinedValueLabel) {
      this.#undefinedValueButton = createButton(this.#undefinedValueLabel, {
        variant: 'borderless',
        onclick: () => {
          this.value = undefined;
          this.#updateColorValueOnChange(this.value);
        },
        styleClass: [`${COMPONENT_NAME}--undefined-value-button`, ...(this.value ? [] : ['is-active'])],
      });
      return this.#undefinedValueButton;
    }
  }

  #createPaletteListElement() {
    const paletteList = new SelectList<Color>(createOptions(this.#palette));
    paletteList.classList.add(`${COMPONENT_NAME}--palette`);
    paletteList.setAttribute('aria-label', i18n('colorList.ariaLabel'));
    paletteList.selection = [toHexColor(this.value ?? DEFAULT_CUSTOM_COLOR)];
    on(paletteList, 'change', () => {
      this.#updateColorValueOnChange(paletteList.selection[0]);
    });
    return paletteList;
  }

  #createCustomColorSection() {
    if (!supportsNativeColorInput()) {
      return;
    }
    let color = toHexColor(this.value ?? DEFAULT_CUSTOM_COLOR);
    const isCustomColor = !!this.value && !this.#palette.some((c) => c === toHexColor(this.value!));
    const colorOption = createColorDisc(color, isCustomColor);
    const colorInput = createColorInput(color, colorOption);
    on(colorInput, 'change', () => {
      this.#updateColorValueOnChange(colorInput.value as Color);
    });
    return h(`label.${COMPONENT_NAME}--custom-color.button.button-borderless`, [
      colorOption,
      colorInput,
      i18n('customColorControl.label'),
    ]);
  }

  #createOpacitySlider(value: Color) {
    const opacitySlider = createOpacitySliderControl(value ? toOpacitySliderValue(getOpacity(value)) : undefined);
    // On macOS the "change" event behaves like an "input" event and fires at each incremental change
    // (e.g. the user is dragging across the color spectrum). Without debouncing this would lead to a
    // huge amount of events.
    on(
      opacitySlider,
      'change',
      debounce(() => this.#updateOpacityValue()),
    );
    return opacitySlider;
  }

  #updateOpacityValue() {
    if (this.#opacitySlider && this.value) {
      this.value = getColorWithOpacity(this.value, getOpacityDecimalValueFromPercentage(this.#opacitySlider.value));
      this.dispatchEvent(new ChangeEvent());
    }
  }

  #createOpacityElement() {
    if (this.#opacitySlider) {
      return createFormGroup(i18n('opacityControl.label'), this.#opacitySlider);
    }
  }

  #updateColorValueOnChange(selectedColor: Color | undefined) {
    if (selectedColor) {
      const opacity = this.value ? getOpacity(this.value) : 1;
      this.value = getColorWithOpacity(selectedColor, opacity);
    } else {
      this.value = selectedColor;
    }
    this.#dispatchChangeEventAndClosePopup();
  }

  #updateStylesOnValueChange() {
    let visualizedColor: Color | undefined = this.#currentColor;
    if (!visualizedColor) {
      visualizedColor = this.#undefinedValueLabel && !this.#iconButton ? 'rgba(0, 0, 0, 0)' : COLORS.COLOR_GRAY_01;
    }
    this.#undefinedValueButton?.classList.toggle('is-active', !this.#currentColor);
    this.#selectedColorElement.style.backgroundColor = visualizedColor!;
    this.#selectedColorElement.classList.toggle(
      'with-outline',
      !!(this.#currentColor && shouldColorSampleHaveOutline(visualizedColor)),
    );
    this.#selectedColorElement.textContent =
      this.#undefinedValueLabel && !this.#currentColor && !this.#iconButton ? this.#undefinedValueLabel : '';
    if (this.#undefinedValueLabel && !this.#currentColor && this.#iconButton) {
      this.#selectedColorElement.setAttribute('title', this.#undefinedValueLabel);
    } else {
      this.#selectedColorElement.removeAttribute('title');
    }
  }

  #resetOpacityControl() {
    if (this.#opacitySlider) {
      this.#opacitySlider.value = this.value ? toOpacitySliderValue(getOpacity(this.value)) : 100;

      this.#opacitySlider.disabled = !this.value;
    }
  }

  /**
   * Sets the selected color of the picker (internally this color is saved in RGBA format).
   * Undefined can be passed here to indicate that no color is selected.
   */
  set value(value: Color | undefined) {
    assert(value || this.#undefinedValueLabel, 'Undefined values are only allowed if undefinedValueLabel has been set');
    this.#currentColor = value ? toRgbaColor(value) : value;
    this.#updateStylesOnValueChange();
    this.#resetOpacityControl();
  }

  /**
   * Gets the selected color of the picker, with opacity if available.
   * Undefined means that no color is selected.
   */
  get value(): Color | undefined {
    if (!this.#currentColor) {
      return this.#currentColor;
    }
    if (this.#returnType === 'rgba') {
      return this.#currentColor;
    } else {
      const currentColorInHex = toHexColor(this.#currentColor);
      return this.#returnType === 'upperHex' ? (currentColorInHex.toUpperCase() as Color) : currentColorInHex;
    }
  }

  get disabled() {
    return ariaTrueFalse(this, 'disabled');
  }

  set disabled(value: boolean) {
    ariaTrueFalse(this, 'disabled', value);
    this.tabIndex = value ? -1 : 0;
  }

  get readOnly() {
    return ariaTrueFalse(this, 'readonly');
  }

  set readOnly(value: boolean) {
    ariaTrueFalse(this, 'readonly', value);
    if (this.isConnected) {
      if (value) {
        this.removeAttribute('role');
        this.tabIndex = -1;
        this.#unregisterEvents();
      } else {
        this.#setupPicker();
      }
    }
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string | null) {
    if (value) {
      this.setAttribute('name', value);
    } else {
      this.removeAttribute('name');
    }
  }
}

export function createOpacitySliderControl(initialOpacity?: number) {
  return new SliderNumberInput({
    defaultValue: initialOpacity ?? 100,
    unit: '%',
  });
}

function createColorDisc(color: Color, isCustomColor: boolean) {
  const colorOption = h('d-color-disc', { color });
  if (isCustomColor) {
    colorOption.append(icon(selectedIcon, { size: 'xs' }));
  }
  return colorOption;
}

function createColorInput(color: Color, colorOption: HTMLElement) {
  return h<HTMLInputElement>('input.visuallyhidden', {
    type: 'color',
    value: color,
    oninput(this: HTMLInputElement) {
      colorOption.style.backgroundColor = this.value;
      colorOption.classList.toggle('dark-bg', isDark(this.value as Color));
    },
  });
}

function createOptions(palette: Color[]) {
  return palette.map((color) =>
    h<SelectListOptionElement<Color>>('d-color-disc', { color, selectListValue: color }, [
      icon(selectedIcon, { size: 'xs' }),
      h('.visuallyhidden', color),
    ]),
  );
}

function supportsNativeColorInput() {
  const input = document.createElement('input');
  try {
    input.type = 'color';
  } catch {
    return false;
  }
  return input.type === 'color';
}

function toOpacitySliderValue(opacity: number) {
  return opacity * 100;
}

customElements.define(COMPONENT_NAME, ColorPicker);
